import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Play, PlayCircle } from 'lucide-react';
import GameCatalog from '../components/GameCatalog';
import PageSlider from '../components/PageSlider';
import TopWinsStrip from '../components/TopWinsStrip';
import GameArtwork from '../components/GameArtwork';
import GameTile from '../components/GameTile';
import GameModal from '../components/GameModal';
import GameChoiceModal from '../components/GameChoiceModal';
import AuthModal from '../components/AuthModal';
import { useGameLauncher } from '../hooks/useGameLauncher';
import { useGameCatalog } from '../hooks/useGameCatalog';
import { useActiveTheme } from '../lib/activeTheme';
import { useSettings } from '../context/SettingsContext';
import { brandName } from '../lib/brand';
import { defaultProviderLogo } from '../lib/providerLogos';
import { isExclusiveGame } from '../lib/exclusiveGame';
import {
  LIVE_CASINO_TABS as tabs, liveCasinoTabFrom, liveCasinoTabPath,
} from '../lib/liveCasinoTabs';

const LiveCasino = () => {
  const g = useGameLauncher();
  const themeId = useActiveTheme();
  // Rozet markaya bağlıdır; ad ayar kaydından gelir, koda gömülmez.
  const brand = brandName(useSettings());
  // Sekme adresten okunur (`?masa=`): Betrun'un sol menüsündeki alt
  // maddeler doğrudan bir sekmeye bağlanır.
  const location = useLocation();
  const navigate = useNavigate();
  const tab = liveCasinoTabFrom(location.search);
  const setTab = (id) => navigate(liveCasinoTabPath(id), { replace: true });
  const keywords = tabs.find((t) => t.id === tab)?.keywords || '';
  const catalog = useGameCatalog('live-casino', { keywords });

  const renderClassicTile = (item) => {
    const logo = defaultProviderLogo(item.provider);
    return (
    <div key={item.id} className="game-tile game-tile--portrait" onClick={() => g.play(item)}>
      <GameArtwork src={item.image} alt={item.name} />
      {/* Sol üstte masanın sağlayıcısı durur. Logo çözülemeyen sağlayıcıda
          CANLI rozeti kalır: boş bir köşe bırakmaktansa sayfanın kendi
          bilgisini yazmak yeğdir. */}
      {logo ? (
        <span className="game-provider-badge z-10">
          <img src={logo} alt={item.provider || ''} loading="lazy" />
        </span>
      ) : (
        <span className="absolute top-2 left-2 live-badge z-10"><span className="live-dot" />CANLI</span>
      )}
      {/* Bayiye özel masa: adında kurulumun markası geçer (lib/exclusiveGame.js). */}
      {isExclusiveGame(item.name, brand) && (
        <span className="game-exclusive-badge z-10">Özel</span>
      )}
      <div className="overlay">
        <button className="gold-btn rounded-full w-14 h-14 flex items-center justify-center">
          <Play size={22} fill="var(--pusu-on-accent)" />
        </button>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-2.5 z-10" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.9))' }}>
        <div className="text-sm font-semibold truncate">{item.name}</div>
        <div className="text-xs text-gray-400 truncate">{item.provider}</div>
      </div>
    </div>
    );
  };

  // Herkül'de masa kartı şemanın kendi kartıdır. Sayfa `renderTile` ile kendi
  // masasını çizdiği için ana sayfadaki vitrin ile katalog aynı oyunu iki
  // farklı görünümde gösteriyordu; kart artık ortak kapıdan (`GameTile`)
  // geçer ve `live` bayrağı CANLI rozetini açar. Betrun da aynı yoldadır:
  // canlı masalar slot kataloğuyla aynı kartta ve aynı üçlü ızgarada
  // (`GameCatalog.BETRUN_GRID`) dizilir — dikey dörtlü masa kartları telefonda
  // iki sütuna düşüyor, slot sayfasıyla ayrı bir site gibi görünüyordu.
  // Diğer şemalar klasik masayı çizmeye devam eder.
  const sharedCard = themeId === 'herkul' || themeId === 'betrun';
  const renderTile = (item) => (
    sharedCard
      ? <GameTile key={item.id} game={{ ...item, img: item.image, live: true }} onPlay={g.play} />
      : renderClassicTile(item)
  );

  return (
    <div className="pusu-body-gradient">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Banner Yönetimi → konum "Canlı Casino Sayfası". */}
        <PageSlider placement="live-casino" />

        <div className="catalog-heading flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--pusu-gold-rgb),0.12)' }}>
            <PlayCircle size={22} className="text-[var(--pusu-gold)]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black gold-text">Canlı Casino</h1>
            <p className="text-sm text-gray-400">Gerçek krupiyeler eşliğinde canlı casino heyecanı</p>
          </div>
        </div>

        <GameCatalog
          catalog={catalog}
          chips={tabs}
          chipValue={tab}
          onChipChange={setTab}
          searchPlaceholder="Masa ara..."
          emptyText="Bu filtrelerle masa bulunamadı."
          gridClassName={sharedCard ? undefined : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'}
          renderTile={renderTile}
        />

        <TopWinsStrip category="live-casino" />
      </div>

      <GameChoiceModal
        game={g.choice} user={g.user} balance={g.balance} bonusBalance={g.bonusBalance}
        demoAvailable={g.demoAvailable}
        onClose={g.closeChoice} onReal={g.startReal} onDemo={g.startDemo}
        onLogin={() => g.openAuth('login')} onRegister={() => g.openAuth('register')}
      />
      {g.activeGame && (
        <GameModal key={`${g.activeGame.id}-${g.mode}`} game={g.activeGame}
                   mode={g.mode} onClose={g.closeGame} />
      )}
      <AuthModal open={!!g.authTab} onClose={() => g.setAuthTab(null)} defaultTab={g.authTab || 'login'} />
    </div>
  );
};

export default LiveCasino;
